/**
 * Terminy zleceń w formularzach osi zleceń — czas polski.
 *
 * ## Konwencja (28.09.2026, zastępuje „cyfry polskie jako UTC” z #287)
 *
 * W bazie jest prawdziwe UTC: import TMS przelicza czas polski bez strefy na
 * chwilę, prezlecenie też (API: `apps/api/src/shared/client-time.ts`). API
 * oddaje więc `2026-10-01T06:00:00.000Z` dla okna 08:00 polskiej (lato).
 * Ekran i formularz pokazują czas polski — strefę Warszawy wprost, nie strefę
 * przeglądarki (`shared/dateTime.ts`).
 *
 * ## Formularz
 *
 * `datetime-local` daje czas bez strefy i jest to czas polski. Do API
 * wysyłamy go z przesunięciem Warszawy w tym dniu (`+02:00` latem, `+01:00`
 * od 25.10.2026) — to jest chwila, której nie da się źle odczytać,
 * niezależnie od strefy przeglądarki i serwera. Godzinę podwójną (25.10,
 * 02:30) i nieistniejącą (28.03, 02:30) rozstrzygamy jak serwer i migracja
 * w PostgreSQL: przesunięciem zimowym (+01:00).
 */

import { CLIENT_TIME_ZONE, dateTimeNoSeconds, warsawParts } from './dateTime';

const ZONE = CLIENT_TIME_ZONE;

const pad = (n: number) => String(n).padStart(2, '0');

const NAIVE_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

const formatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** Przesunięcie Warszawy względem UTC w danej chwili, w minutach. */
function offsetMinutesAt(instantMs: number): number {
  const parts: Record<string, number> = {};
  for (const p of formatter.formatToParts(new Date(instantMs))) {
    if (p.type !== 'literal') parts[p.type] = Number(p.value);
  }
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour === 24 ? 0 : parts.hour,
    parts.minute,
    parts.second,
  );
  return Math.round((asUtc - instantMs) / 60_000);
}

const parse = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Chwila z API → wartość pola `datetime-local` (czas polski). */
export function toDateTimeLocalValue(iso?: string | null): string | null {
  const d = parse(iso);
  if (!d) return null;
  const w = warsawParts(d);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}`;
}

/**
 * Wartość pola `datetime-local` (czas polski) → chwila z przesunięciem.
 *
 * Pusty tekst zostaje pusty, a tekst, który nie jest czasem z formularza,
 * idzie bez zmian — niech serwer powie, co z nim nie tak, zamiast żeby
 * przeglądarka go po cichu zgubiła.
 */
export function fromDateTimeLocalValue(value?: string | null): string | null {
  if (value === null || value === undefined) return null;
  const t = value.trim();
  if (t === '') return null;
  const m = NAIVE_RE.exec(t);
  if (!m) return t;
  const wall = Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    Number(m[6] ?? 0),
  );
  // Przesunięcie liczone dla chwili, a chwila zależy od przesunięcia —
  // druga próba rozstrzyga noce zmiany czasu (godzina podwójna i dziura
  // wychodzą z przesunięciem zimowym, jak w PostgreSQL i w API).
  let offset = offsetMinutesAt(wall);
  const second = offsetMinutesAt(wall - offset * 60_000);
  if (second !== offset) offset = second;
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] ?? '00'}${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/** „01.10.2026, 08:00” — termin zlecenia w czasie polskim; brak = „—”. */
export function wallClockDateTime(iso?: string | null): string {
  return dateTimeNoSeconds(iso);
}
